import type {
  RecommendedDurationBucket,
  RecommendedQuizResult,
  RecommendedQuizzesResponse,
  RecommendedSummaryCardExhausted,
  RecommendedUpsellPlan
} from '@api/fetchers/goal/test-tab/types';

export type RecommendedSummaryState =
  | 'available'
  | 'exhausted_prime'
  | 'exhausted_plus';

interface RecommendedDurationOptionBase {
  durationBucket: RecommendedDurationBucket;
  label: string;
  description: string | null;
  iconUrl: string | null;
  cta: string | null;
}

export interface RecommendedAvailableOption
  extends RecommendedDurationOptionBase {
  isExhausted: false;
  quizUid: string;
  href: string;
}

export interface RecommendedExhaustedOption
  extends RecommendedDurationOptionBase {
  isExhausted: true;
  upsellPlan: RecommendedUpsellPlan;
}

export type RecommendedDurationOption =
  | RecommendedAvailableOption
  | RecommendedExhaustedOption;

export interface RecommendedExhaustedSummary {
  plan: RecommendedUpsellPlan;
  eyebrow: string | null;
  title: string | null;
  cta: string | null;
}

export interface RecommendedCardData {
  sectionTitle: string | null;
  summaryState: RecommendedSummaryState;
  exhausted: RecommendedExhaustedSummary | null;
  eyebrow: string | null;
  titleCollapsed: string | null;
  titleExpanded: string | null;
  ctaCollapsed: string | null;
  ctaExpanded: string | null;
  dividerText: string | null;
  options: RecommendedDurationOption[];
}

interface RecommendedUserState {
  isPlusOrIconicUser: boolean;
  isPrimeQuotaExhausted: boolean;
  isPlusTabSelected?: boolean;
}

const GOAL_NAME_TOKEN = /\{goal_name\}/g;

const withGoalName = (text: string | null | undefined, goalName: string) =>
  text ? text.replace(GOAL_NAME_TOKEN, goalName) : null;

const getResultHref = (result: RecommendedQuizResult) =>
  result.relativeLink ||
  result.permalink ||
  (result.quizUid ? `/quiz/test/${result.quizUid}` : null);

const toDurationOption = (
  result: RecommendedQuizResult,
  { isPlusOrIconicUser, isPlusTabSelected }: RecommendedUserState
): RecommendedDurationOption | null => {
  if (!result.label) {
    return null;
  }

  const base = {
    durationBucket: result.durationBucket,
    label: result.label,
    iconUrl: result.iconUrl
  };

  if (
    result.durationQuotaExhausted &&
    result.upsellPlan &&
    !isPlusOrIconicUser
  ) {
    const upsellPlan =
      isPlusTabSelected && result.upsellPlan === 'prime'
        ? 'plus'
        : result.upsellPlan;

    return {
      ...base,
      isExhausted: true,
      upsellPlan,
      description: result.exhaustedDescription ?? result.description,
      cta:
        upsellPlan === 'plus'
          ? result.ctaExhaustedPlus
          : result.ctaExhaustedPrime
    };
  }

  const href = getResultHref(result);

  if (!result.quizUid || !href) {
    return null;
  }

  return {
    ...base,
    isExhausted: false,
    quizUid: result.quizUid,
    href,
    description: result.description,
    cta: result.ctaAvailable
  };
};

const getSummaryState = (
  options: RecommendedDurationOption[],
  {
    isPlusOrIconicUser,
    isPrimeQuotaExhausted,
    isPlusTabSelected
  }: RecommendedUserState
): RecommendedSummaryState => {
  if (isPlusOrIconicUser) {
    return 'available';
  }

  // Plus toggle: free and Prime (trial) users see Plus pitch; paid users handled above.
  if (isPlusTabSelected) {
    return 'exhausted_plus';
  }

  const exhaustedOptions = options.filter(
    (option): option is RecommendedExhaustedOption => option.isExhausted
  );
  const allBucketsExhausted =
    exhaustedOptions.length > 0 && exhaustedOptions.length === options.length;

  if (allBucketsExhausted) {
    return exhaustedOptions.some((option) => option.upsellPlan === 'plus')
      ? 'exhausted_plus'
      : 'exhausted_prime';
  }

  if (isPrimeQuotaExhausted) {
    return 'exhausted_prime';
  }

  return 'available';
};

const toExhaustedSummary = (
  card: RecommendedSummaryCardExhausted | null | undefined,
  plan: RecommendedUpsellPlan,
  goalName: string
): RecommendedExhaustedSummary | null =>
  card
    ? {
        plan,
        eyebrow: withGoalName(card.eyebrow, goalName),
        title: withGoalName(card.title, goalName),
        cta: card.cta
      }
    : null;

export const normalizeRecommended = (
  response: RecommendedQuizzesResponse | null | undefined,
  goalName: string,
  userState: RecommendedUserState
): RecommendedCardData | null => {
  const summary = response?.summary;
  const available = summary?.available;
  const options = (response?.results ?? [])
    .map((result) => toDurationOption(result, userState))
    .filter((option): option is RecommendedDurationOption => option !== null);
  const summaryState = getSummaryState(options, userState);

  let exhausted: RecommendedExhaustedSummary | null = null;

  if (summaryState === 'exhausted_prime') {
    exhausted = toExhaustedSummary(summary?.exhaustedPrime, 'prime', goalName);
  } else if (summaryState === 'exhausted_plus') {
    exhausted = toExhaustedSummary(summary?.exhaustedPlus, 'plus', goalName);
  }

  if (summaryState !== 'available' && !exhausted) {
    return null;
  }

  if (summaryState === 'available' && (!available || options.length === 0)) {
    return null;
  }

  return {
    sectionTitle: withGoalName(response?.sectionTitle, goalName),
    summaryState,
    exhausted,
    eyebrow: withGoalName(available?.eyebrow, goalName),
    titleCollapsed: withGoalName(available?.titleCollapsed, goalName),
    titleExpanded: withGoalName(available?.titleExpanded, goalName),
    ctaCollapsed: available?.ctaCollapsed ?? null,
    ctaExpanded: available?.ctaExpanded ?? null,
    dividerText: withGoalName(response?.dividerText, goalName),
    options
  };
};
